import { useCallback } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { MatterTemplateService, MatterTemplate } from '../../../services/matterTemplateService';

export const useMatterTemplates = () => {
  const { keycloak } = useKeycloak();

  // Get JWT token from Keycloak (following working pattern)
  const getAuthToken = () => {
    
    if (keycloak?.token) {
      return keycloak.token;
    }
    
    return '';
  };

  const getMatterTemplates = useCallback(async (): Promise<MatterTemplate[]> => {
    try {
      const token = getAuthToken();
      
      if (!token) {
        console.error('❌ [MatterTemplates] No authentication token available');
        return [];
      }

      const templates = await MatterTemplateService.getMatterTemplates(token);
      
      return templates;
    } catch (error) {
      console.error('❌ [MatterTemplates] Error loading templates:', error);
      return [];
    }
  }, [keycloak?.token]);

  const getTemplatesByPracticeArea = useCallback(async (practiceArea: string): Promise<MatterTemplate[]> => {
    try {
      console.log('🔄 [MatterTemplates] Loading templates for practice area:', practiceArea);
      const token = getAuthToken();
      
      if (!token) {
        console.error('❌ [MatterTemplates] No authentication token available');
        return [];
      }

      const templates = await MatterTemplateService.getTemplatesByPracticeArea(token, practiceArea);
      console.log('✅ [MatterTemplates] Loaded', templates.length, 'templates for', practiceArea);
      
      return templates;
    } catch (error) {
      console.error('❌ [MatterTemplates] Error loading templates by practice area:', error);
      return [];
    }
  }, [keycloak?.token]);

  const getTemplateById = useCallback(async (templateId: string): Promise<MatterTemplate | null> => {
    try {
      console.log('🔄 [MatterTemplates] Loading template by ID:', templateId);
      const token = getAuthToken();
      
      if (!token) {
        console.error('❌ [MatterTemplates] No authentication token available');
        return null;
      }

      const template = await MatterTemplateService.getTemplateById(token, templateId);
      console.log('✅ [MatterTemplates] Loaded template:', template?.name || 'Not found');
      
      return template;
    } catch (error) {
      console.error('❌ [MatterTemplates] Error loading template by ID:', error);
      return null;
    }
  }, [keycloak?.token]);

  const createMatterTemplate = useCallback(async (template: Omit<MatterTemplate, 'id' | 'createdAt'>): Promise<MatterTemplate | null> => {
    try {
      console.log('🔄 [MatterTemplates] Creating new template:', template.name);
      const token = getAuthToken();
      
      if (!token) {
        console.error('❌ [MatterTemplates] No authentication token available');
        throw new Error('Authentication required');
      }

      const createdTemplate = await MatterTemplateService.createMatterTemplate(token, template);
      console.log('✅ [MatterTemplates] Created template:', createdTemplate.name);
      
      return createdTemplate;
    } catch (error) {
      console.error('❌ [MatterTemplates] Error creating template:', error);
      throw error;
    }
  }, [keycloak?.token]);

  const updateMatterTemplate = useCallback(async (templateId: string, updates: Partial<MatterTemplate>): Promise<MatterTemplate | null> => {
    try {
      console.log('🔄 [MatterTemplates] Updating template:', templateId);
      const token = getAuthToken();
      
      if (!token) {
        console.error('❌ [MatterTemplates] No authentication token available');
        throw new Error('Authentication required');
      }

      const updatedTemplate = await MatterTemplateService.updateMatterTemplate(token, templateId, updates);
      console.log('✅ [MatterTemplates] Updated template:', updatedTemplate.name);
      
      return updatedTemplate;
    } catch (error) {
      console.error('❌ [MatterTemplates] Error updating template:', error);
      throw error;
    }
  }, [keycloak?.token]);

  const deleteMatterTemplate = useCallback(async (templateId: string): Promise<void> => {
    try {
      console.log('🔄 [MatterTemplates] Deleting template:', templateId);
      const token = getAuthToken();
      
      if (!token) {
        console.error('❌ [MatterTemplates] No authentication token available');
        throw new Error('Authentication required');
      }

      await MatterTemplateService.deleteMatterTemplate(token, templateId);
      console.log('✅ [MatterTemplates] Deleted template:', templateId);
      
    } catch (error) {
      console.error('❌ [MatterTemplates] Error deleting template:', error);
      throw error;
    }
  }, [keycloak?.token]);

  return {
    getMatterTemplates,
    getTemplatesByPracticeArea,
    getTemplateById,
    createMatterTemplate,
    updateMatterTemplate,
    deleteMatterTemplate
  };
}; 